import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { UserCheck, Building2, Shield, Clock, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { InvitationErrorBoundary } from "@/components/InvitationErrorBoundary";

interface InvitationData {
  id: string;
  email: string;
  role: string;
  company_name: string;
  invited_by_name: string;
  expires_at: string;
  created_at: string;
}

function AcceptInvitationContent() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError("Token de convite inválido");
        setLoading(false);
        return;
      }

      try {
        const { data, error: rpcError } = await supabase
          .rpc('get_invitation_details_by_token', { p_token: token });

        if (rpcError) {
          setError(`Erro ao buscar convite: ${rpcError.message}`);
          setLoading(false);
          return;
        }

        const status = data?.status as string | undefined;

        if (status === 'invalid' || status === 'not_found') {
          setError("Convite não encontrado ou inválido");
          setLoading(false);
          return;
        }

        if (status === 'expired') {
          setError("Este convite expirou. Solicite um novo convite ao administrador.");
          setLoading(false);
          return;
        }

        if (status === 'cancelled') {
          setError("Este convite foi cancelado.");
          setLoading(false);
          return;
        }

        if (status === 'accepted') {
          toast.info("Este convite já foi aceito. Faça login com suas credenciais.");
          navigate('/auth');
          return;
        }

        if (status === 'pending') {
          setInvitation({
            id:              data.id,
            email:           data.email,
            role:            data.role,
            company_name:    data.company_name,
            invited_by_name: data.invited_by_name,
            expires_at:      data.expires_at,
            created_at:      data.created_at,
          });
        } else {
          setError("Convite inválido ou expirado");
        }
      } catch (err) {
        setError(`Erro inesperado: ${err instanceof Error ? err.message : 'Erro desconhecido'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token, navigate]);

  const handleAcceptInvitation = async () => {
    if (!invitation) return;

    if (!fullName || !password || !confirmPassword) {
      toast.error("Preencha todos os campos");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    if (password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    setSubmitting(true);

    try {
      const signUpResult = await supabase.auth.signUp({
        email: invitation.email,
        password,
        options: {
          data: { full_name: fullName },
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });

      if (signUpResult.error) throw signUpResult.error;

      if (!signUpResult.data.user) {
        throw new Error('Falha ao criar usuário');
      }

      toast.success("Conta criada com sucesso! Processando convite...");

      // Aguarda o trigger processar o convite e faz polling via RPC
      await new Promise(resolve => setTimeout(resolve, 2000));

      for (let attempt = 1; attempt <= 5; attempt++) {
        const { data: statusData } = await supabase
          .rpc('get_invitation_details_by_token', { p_token: token });

        if (statusData?.status === 'accepted') {
          toast.success("Convite aceito com sucesso! Redirecionando...");
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData?.session) {
            setTimeout(() => navigate('/dashboard'), 1000);
          } else {
            toast.info("Por favor, faça login com suas credenciais");
            setTimeout(() => navigate('/auth'), 1000);
          }
          return;
        }

        if (attempt < 5) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      toast.error("O convite foi aceito mas houve um problema no processamento. Tente fazer login.");
      setTimeout(() => navigate('/auth'), 2000);

    } catch (err: any) {
      if (err.message?.includes('User already registered') || err.message?.includes('already been registered')) {
        toast.error("Este email já está cadastrado. Tente fazer login.");
        setTimeout(() => navigate('/auth'), 1000);
      } else if (err.message?.includes('email_confirm')) {
        toast.info("Verifique seu email para confirmar a conta antes de prosseguir.");
      } else {
        toast.error(err.message || "Erro ao processar convite");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(dateString));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-4 h-4 bg-primary rounded-full animate-pulse" />
              <div className="w-4 h-4 bg-primary rounded-full animate-pulse animation-delay-150" />
              <div className="w-4 h-4 bg-primary rounded-full animate-pulse animation-delay-300" />
            </div>
            <p className="text-muted-foreground">Carregando convite...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="mb-4">
              <Clock className="mx-auto h-12 w-12 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Convite Inválido</h2>
            <p className="text-muted-foreground mb-4">{error || "Convite não encontrado"}</p>
            <div className="space-y-2">
              <Button onClick={() => navigate('/auth')} variant="outline" className="w-full">
                Ir para Login
              </Button>
              <Button onClick={() => window.location.reload()} variant="ghost" className="w-full">
                Tentar Novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl space-y-6">
        {/* Invitation Details */}
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <UserCheck className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-2xl">Você foi convidado!</CardTitle>
            <CardDescription>
              Complete seu cadastro para acessar o sistema
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 border rounded-lg">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{invitation.company_name}</p>
                  <p className="text-sm text-muted-foreground">Empresa</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 border rounded-lg">
                <Shield className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Badge variant={invitation.role === 'master' ? 'default' : 'secondary'}>
                    {invitation.role === 'master' ? 'Administrador' : 'Operador'}
                  </Badge>
                  <p className="text-sm text-muted-foreground">Seu perfil</p>
                </div>
              </div>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              Convidado por <strong>{invitation.invited_by_name}</strong> em {formatDate(invitation.created_at)}
            </div>
          </CardContent>
        </Card>

        {/* Registration Form */}
        <Card>
          <CardHeader>
            <CardTitle>Complete seu cadastro</CardTitle>
            <CardDescription>
              Email: <strong>{invitation.email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full-name">Nome completo *</Label>
              <Input
                id="full-name"
                placeholder="Digite seu nome completo"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha *</Label>
              <Input
                id="password"
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar senha *</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Digite a senha novamente"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            <Button
              onClick={handleAcceptInvitation}
              disabled={submitting || !fullName || !password || !confirmPassword}
              className="w-full"
            >
              {submitting ? "Criando conta..." : "Aceitar convite e criar conta"}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              Ao aceitar este convite, você concorda em fazer parte da equipe da {invitation.company_name}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function AcceptInvitation() {
  return (
    <InvitationErrorBoundary
      fallback={
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-4" />
              <h2 className="text-xl font-semibold mb-2">Erro na Página</h2>
              <p className="text-muted-foreground mb-4">
                Não foi possível carregar a página de convite.
              </p>
              <div className="space-y-2">
                <Button onClick={() => window.location.reload()} className="w-full">
                  Recarregar Página
                </Button>
                <Button onClick={() => window.location.href = '/'} variant="outline" className="w-full">
                  Voltar ao Início
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    >
      <AcceptInvitationContent />
    </InvitationErrorBoundary>
  );
}
